/* eslint-disable */
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { urls } from '@/Constants';
import GlobalSearch from '@/Components/globalSearch/search';
import { ListTable } from '@/Components/ListTable';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { analyticsAction } from '@/Actions';
import { Filter, RefreshCcw } from 'lucide-react';
import ImagesFinder from '@/Components/ImagesFinder/ImagesFinder';
import drawerActions from '@/Actions/drawer.actions';
import { Button } from '@/Components/ui/button';
import { Tooltip } from '@mui/material';
import { filtersInfoChip, getStatusStyle, handleResponseStreamDownload, shipmentStatus } from '@/Helpers';
import moment from 'moment';
import inventoryAnalyticsServices from '@/Services/Analytics Services/inventory-analytics.services';

const DATE_TYPES = [
	{
		label: 'Dispatch Date',
		value: 'dispatchedAt',
	},
	{
		label: 'Receiving Date',
		value: 'receivedAt',
	},
	{
		label: 'Scheduled Date',
		value: 'expectedDispatchDate',
	},
	{
		label: 'Created On',
		value: 'createdAt',
	},
];

const statusList = [
	{
		title: 'Packaging',
		id: 'PACKAGING',
	},
	{
		title: 'Dispatch',
		id: 'DISPATCH',
	},
	{
		title: 'Transit',
		id: 'TRANSIT',
	},
	{
		title: 'Verification',
		id: 'VERIFICATION',
	},
	{
		title: 'Fulfilled',
		id: 'FULFILLED',
	},
	{
		title: 'Cancelled',
		id: 'CANCELLED',
	},
	{
		title: 'Unloading',
		id: 'UNLOADING',
	},
	{
		title: 'Abandoned',
		id: 'ABANDONED',
	},
]
// SPECIAL_COLUMNS for the shipment list table
const SPECIAL_COLUMNS = [
	{
		title: 'UY Id',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Client Shipment Id',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Client PO Id',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Deliver From',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Deliver To',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Scheduled Date',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Dispatch Date',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Receiving Date',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Created On',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Shipment Type',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Vehicle Number',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Invoice / DC',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Invoice Amount',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Status',
		config: {
			type: 'DATA',
		},
	},
];

const shipmentColMap = {
	'UY Id': 'id',
	'Client Shipment Id': 'clientShipmentId',
	'Client PO Id': 'clientPoId',
	'Deliver From': 'sendingStoreName',
	'Deliver To': 'receivingStoreName',
	'Scheduled Date': 'expectedDispatchDate',
	'Created On': 'createdAt',
	'Dispatch Date': 'sendingDate',
	'Receiving Date': 'receivingDate',
	'Shipment Type': 'shipmentType',
	'Vehicle Number': 'transVehicleNo',
	'Invoice / DC': 'sendingDocId',
	'Invoice Amount': 'dispatchedValue',
	'Status': 'status',
};

const dropDownItems = [
	{
		label: 'Client Shipment ID',
		value: 'clientShipmentId',
		placeholder: 'Search by Client Shipment ID',
		getData: true,
	},
	{
		label: 'Invoice No',
		value: 'invoiceNo',
		placeholder: 'Search by Invoice No',
		getData: true,
	},
	{
		label: 'UY Id',
		value: 'id',
		placeholder: 'Search by UY Id',
		getData: true,
	},
	{
		label: 'Client PO ID',
		value: 'clientPoId',
		placeholder: 'Search by Client PO ID',
		getData: true,
	},
	{
		label: 'Vehicle Number',
		value: 'transVehicleNo',
		placeholder: 'Search by Vehicle Number',
		disable: true,
		getData: true,
	},
];

function ShipmentList({ shipmentCategory }) {
	const history = useHistory();
	const pageTitle =
		shipmentCategory === 'buyer'
			? 'Buyer Shipment Logs'
			: shipmentCategory === 'internal'
				? 'Internal Shipment Logs'
				: 'Shipment Logs';
	// Maps the Deliver From / Deliver To / Buyer filter selections into request fields.
	// Buyer: 'Deliver From' (store) -> fromStoreId, Buyer -> buyers.
	// Internal: 'Deliver From' (deliverFrom) -> fromStoreId, 'Deliver To' (store) -> storeId.
	const buildScopeFilters = (f) => {
		const normalizeStore = (s) => (s?.id === -999 ? null : s?.id);
		if (shipmentCategory === 'buyer') {
			return {
				fromStoreId: normalizeStore(f?.store),
				buyers: f?.buyer?.id,
			};
		}
		if (shipmentCategory === 'internal') {
			return {
				fromStoreId: normalizeStore(f?.deliverFrom),
				storeId: normalizeStore(f?.store),
			};
		}
		return {};
	};
	const dispatch = useDispatch();
	const location = useLocation();
	const [searchValue, setSearchValue] = useState({});
	const [filter, setFilters] = useState({});
	const [refresh, setRefresh] = useState(false);
	const shipments = useSelector((state) => state.analytics.INVENTORY_ANALYTICS.SHIPMENT_LIST);

	const handleFetchShipmentList = (filters, isRefresh = false) => {
		try {
			setFilters(filters);
			setRefresh(isRefresh);
			const req = {
				data: {
					filters: {
						dateRangeFilter: {
							from: moment(filters?.dateRange?.from).format(
								'YYYY-MM-DD'
							),
							to: moment(filters?.dateRange?.to).format(
								'YYYY-MM-DD'
							),
							dateType: filters?.dateRange?.dateType,
						},
						statuses: filters?.status,
						shipmentType: filters?.type,
						shipmentCategory,
						...buildScopeFilters(filters),
					},
				}
			}
			dispatch(analyticsAction.gateway({
				entity: 'INVENTORY_ANALYTICS',
				method: 'SHIPMENT_LIST',
				req,
			}));
		} catch (error) {
			console.log(error, 'error');
		}
	}

	const shipmentSpecialMappings = {
		'UY Id': (id, items) => {
			return <p className='text-[12px] text-left'>{items ? items : 'N/A'}</p>;
		},
		'Client Shipment Id': (id, items) => {
			return <p className='text-[12px] text-left'>{items ? items : 'N/A'}</p>;
		},
		'Client PO Id': (id, items) => {
			return <p className='text-[12px] text-left'>{items ? items : 'N/A'}</p>;
		},
		'Deliver From': (id, items) => {
			return <p className='text-[12px] text-left'>{items ? items : 'N/A'}</p>;
		},
		'Deliver To': (id, items) => {
			return <p className='text-[12px] text-left'>{items ? items : 'N/A'}</p>;
		},
		'Dispatch Date': (id, items) => {
			return <p className='text-[12px] text-left'>{items ? moment(items).format('DD-MM-YYYY') : 'N/A'}</p>;
		},
		'Receiving Date': (id, items) => {
			return <p className='text-[12px] text-left'>{items ? moment(items).format('DD-MM-YYYY') : 'N/A'}</p>;
		},
		'Scheduled Date': (id, items) => {
			return <p className='text-[12px] text-left'>{items ? moment(items).format('DD-MM-YYYY') : 'N/A'}</p>;
		},
		'Created On': (id, items) => {
			return <p className='text-[12px] text-left'>{items ? moment(items).format('DD-MM-YYYY') : 'N/A'}</p>;
		},
		'Shipment Type': (id, items) => {
			return <p className='text-[12px] text-left'>{items ? items : 'N/A'}</p>;
		},
		'Vehicle Number': (id, items) => {
			return <p className='text-[12px] text-left'>{items ? items : 'N/A'}</p>;
		},
		'Status': (id, items) => {
			return <p className='text-[12px] text-left' style={getStatusStyle(items)}>{items ? items : 'N/A'}</p>;
		},
		'Invoice / DC': (id, items) => {
			return <p className='text-[12px] text-left'>{items ? items : 'N/A'}</p>;
		},
		'Invoice Amount': (id, items) => {
			return <p className='text-[12px] text-left'>{items ? `₹${items.toFixed(2)}` : 'N/A'}</p>;
		},
	};


	const filtersInfo = {
		header: {
			store: false,
			date: false,
			statusAndTypes: false,
			product: false,
		},
		...(shipmentCategory === 'buyer'
			? {
				store: {
					text: 'Deliver From',
					show: true,
					permittedStore: true,
				},
				buyer: {
					text: 'Buyer',
					show: true,
				},
			}
			: {
				deliverFrom: {
					text: 'Deliver From',
					show: true,
				},
				store: {
					text: 'Deliver To',
					show: true,
				},
			}),
		dateRangeWithType: {
			text: 'Date Range',
			show: true,
		},
		status: {
			text: 'Status',
			show: true,
		},
		timeType: {
			text: 'Date Time Type',
			show: true,
		},
		type: {
			text: 'Shipment Type',
			show: true,
			list: [
				{
					label: 'ADDHOC',
					type: 'ADDHOC',
				},
				{
					label: 'PLANNED',
					type: 'PLANNED',
				},
				{
					label: 'WHOLE ORDER',
					type: 'WHOLE_ORDER',
				},
			]
		},
	};

	const exportInfo = {
		showDropDown: true,
		showExportBtn: true,
		exportTypes: [
			{
				label: 'Item-Wise',
				value: 'itemwise',
			},
			{
				label: 'Shipment-Wise',
				value: 'shipmentwise',
			},
		],
	};

	const idSearch = () => {
		dispatch(analyticsAction.gateway({
			entity: 'INVENTORY_ANALYTICS',
			method: 'SHIPMENT_LIST',
			req: {
				data: {
					search: {
						...(searchValue?.value === 'id' ? { id: searchValue?.searchValue } : {}),
						...(searchValue?.value === 'transVehicleNo' ? { vehicleNo: searchValue?.searchValue } : {}),
						...(searchValue?.value === 'clientShipmentId' ? { clientShipmentId: searchValue?.searchValue } : {}),
						...(searchValue?.value === 'clientPoId' ? { clientPoId: searchValue?.searchValue } : {}),
						...(searchValue?.value === 'invoiceNo' ? { invoiceNo: searchValue?.searchValue } : {}),
					},
				},
			},
		}));
	}

	useEffect(() => {
		if (searchValue?.keyPress && searchValue?.searchValue?.trim() === '') {
			handleFetchShipmentList(filter);
		}
	}, [searchValue?.searchValue]);

	const handleCall = (e) => {
		if (e.key === 'Enter') {
			idSearch();
		}
	}

	const handleShipmentExport = async (exportFilters) => {
		try {
			const req = {
				data: {
					filters: {
						dateRangeFilter: {
							from: moment(exportFilters?.dateRange?.from).format(
								'YYYY-MM-DD'
							),
							to: moment(exportFilters?.dateRange?.to).format(
								'YYYY-MM-DD'
							),
							dateType: exportFilters?.dateRange?.dateType,
						},
						statuses: exportFilters?.status,
						shipmentType: exportFilters?.type,
						shipmentCategory,
						...buildScopeFilters(exportFilters),
					},
					exportType: exportFilters?.exportType,
				},
			}
			const response = await inventoryAnalyticsServices?.exportShipmentList(req);
			const fromDate = moment(exportFilters?.dateRange?.from).format('DD-MM-YYYY');
			const toDate = moment(exportFilters?.dateRange?.to).format('DD-MM-YYYY');
			const filePrefix = shipmentCategory ? `${shipmentCategory}-shipment-list` : 'shipment-list';
			const fileName = `${filePrefix}-${fromDate}_to_${toDate}.csv`;
			handleResponseStreamDownload(response, fileName);

		} catch (error) {
			console.log(error, 'error');
		}
	}

	return (
		<>
			<ImagesFinder
				filtersInfo={filtersInfo}
				exportInfo={exportInfo}
				onSubmit={handleFetchShipmentList}
				timeTypeArray={DATE_TYPES}
				statusList={statusList.map((obj) => ({
					...obj,
					title: shipmentStatus(obj?.id),
				}))}
			/>

			<div className='flex flex-col gap-2'>
				<p className='text-[16px] font-bold text-start ml-[1rem] mt-[12px]'>{pageTitle}</p>
				<div className='flex items-center gap-3 justify-center'>
					<div className='w-[82%]'>
						<GlobalSearch
							dropDownOptions={dropDownItems}
							setSelectedValue={setSearchValue}
							handleValue={handleCall}
							width='150px'
							isRefresh={refresh}
						/>
					</div>
					<Tooltip title='Refresh Shipment List'>
						<RefreshCcw
							className='size-5 cursor-pointer'
							onClick={(e) => {
								handleFetchShipmentList(filter, true);
							}}
						/>
					</Tooltip>
					<Button
						onClick={(e) => {
							dispatch(drawerActions?.toggleFiltersDrawer(true, {
								handleDataExport: (exportFilters) => handleShipmentExport(exportFilters),
							}));
						}}
						className='flex justify-center gap-2 px-4 py-2 text-white bg-teal-600 border border-gray-300 rounded-md hover:bg-teal-700'
					>
						<span className='text-nowrap'>Filter & Export</span>
						<Filter className='size-4' />
					</Button>
				</div>
				{filtersInfoChip(filtersInfo, filter)}
				<div className="overflow-auto" style={{ maxHeight: '70vh' }}>
					<ListTable
						data={shipments.map((obj) => {
							return {
								...obj,
								status: shipmentStatus(obj?.status),
								onClick: () => {
									history.push({
										pathname: `${urls.EXTENSION}${urls.SHIPMENT_DETAILS}`,
										search: `?id=${obj?.id}`,
										state: {
											prevPath: location.pathname + location.search,
										},
									});
								},
							};
						})}
						colMap={shipmentColMap}
						actions={SPECIAL_COLUMNS}
						special={shipmentSpecialMappings}
						showBtnContainer={false}
					/>
				</div>
			</div>

		</>
	);
}
export default ShipmentList;